import React, { FC, useLayoutEffect } from 'react';
import { OpeningApplicantCard, Skeleton, Placeholder } from '@src/containers/Feed/OpeningApplicantCard';
import { useAppDispatch } from '@src/redux';
import { setModalContent } from '@src/redux/actions/modal';
import { useQueryParams } from '@src/customHooks/useQueryParams';
import { isVerifiedUserApplicantType } from '@src/containers/Feed/OpeningApplicantCard/typeGuards/isVerifiedUserApplicantType';
import { isOpeningApplicantType } from '@src/containers/Feed/OpeningApplicantCard/typeGuards/isOpeningApplicantType';
import { InviteToInterview } from '@src/components/VerifyTalentCard/InviteToInterview';
import {
  ApplicantsSortOptionEnum,
  ApplicationSourceEnum,
  MessageThreadStateEnum,
  RecruiterApplicationStateEnum,
  UserApplicationStateEnum,
  useRejectApplicationMutation,
} from '@src/graphql/generated';
import { trackEvent } from '@src/metrics';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { QueryParams } from '../interfaces/queryParams.interface';
import { ActiveView } from '../enums/activeView.enum';
import { useGetOpeningApplicants } from '../hooks/useGetOpeningApplicants';
import { useGetOpeningId } from '../hooks/useGetOpeningId';
import { onRejectParams } from '../interfaces/onReject.interface';
import { InConversationHeader } from './InConversationHeader';
import { inConversationProps } from './props';
import './styles.scss';

export const InConversation: FC<inConversationProps> = () => {
  const openingId = useGetOpeningId();

  const dispatch = useAppDispatch();

  const { queryParams, updateQueryParams } = useQueryParams<QueryParams>();

  const sortOption = queryParams.inConversationUsersSortOption || ApplicantsSortOptionEnum.USER_APPLIED_AT;

  const {
    openingApplicants,
    openingApplicantsPagination,
    isOpeningApplicantsLoading,
    loadMoreOpeningApplicants,
    refetchOpeningApplicants,
  } = useGetOpeningApplicants({
    fetchPolicy: 'network-only',
    firstBatchSize: 6,
    nextBatchSize: 3,
    recruiterMessageThreadStates: [MessageThreadStateEnum.OPEN],
    recruiterStates: [RecruiterApplicationStateEnum.ACCEPTED],
    sortBy: sortOption,
    sources: [ApplicationSourceEnum.OPENING, ApplicationSourceEnum.USER_VERIFICATION],
    userStates: [UserApplicationStateEnum.ACCEPTED],
  });

  const openVerifiedTalent = (userId: string): void => {
    dispatch(
      setModalContent({
        className: 'opening-in-conversation__modal-wrapper',
        config: {
          withoutPadding: true,
        },
        content: (
          <InviteToInterview
            areVerificationStagesHidden
            shouldFetchApplicant
            isContactInfoVisible
            modalOptions={{
              shouldOnlyShowProfile: true,
            }}
            openingId={openingId}
            userId={userId}
          />
        ),
      }),
    );
  };

  const openOpeningApplicant = (applicationId: string): void => {
    updateQueryParams({
      applicationId: applicationId,
    });
  };

  const [rejectApplication] = useRejectApplicationMutation({
    onCompleted: async () => {
      refetchOpeningApplicants();
    },
    onError: () => {},
  });

  const onReject = async ({ userMatchingProfileId, openingId }: onRejectParams) => {
    await rejectApplication({
      variables: {
        openingId: openingId,
        userMatchingProfileId: userMatchingProfileId,
      },
    });

    trackEvent({
      context: MetricContext.OPENING_VIEW,
      interaction: MetricAction.REJECT_APPLICATION,
      payload: {
        'Event Source': queryParams?.activeView,
        'Opening Id': openingId,
        'User Matching Profile Id': userMatchingProfileId,
      },
    });
  };

  useLayoutEffect(() => {
    const checkScrollPositionAndLoadMore = () => {
      const {
        body: { scrollHeight },
      } = document;
      const { innerHeight, scrollY } = window;
      const distanceFromBottom = scrollHeight - innerHeight - scrollY;

      if (!isOpeningApplicantsLoading && openingApplicantsPagination?.hasMoreResults && distanceFromBottom < 100) {
        loadMoreOpeningApplicants();
      }
    };

    window.addEventListener('scroll', checkScrollPositionAndLoadMore);

    return () => {
      window.removeEventListener('scroll', checkScrollPositionAndLoadMore);

      loadMoreOpeningApplicants.cancel();
    };
  }, [isOpeningApplicantsLoading, openingApplicantsPagination?.hasMoreResults]);

  return (
    <div className="opening-in-conversation">
      <div className="opening-in-conversation__header">
        <InConversationHeader />
      </div>

      <div className="opening-in-conversation__cards">
        {!openingApplicants?.length && !isOpeningApplicantsLoading && (
          <>
            <Placeholder
              message="Invite candidates to start a conversation"
              onClick={() =>
                updateQueryParams({
                  activeView: ActiveView.INVITE_CANDIDATES,
                })
              }
              wrapperClassName="opening-in-conversation__card"
            />
            <Placeholder wrapperClassName="opening-in-conversation__card" />
            <Placeholder wrapperClassName="opening-in-conversation__card" />
          </>
        )}

        {openingApplicants?.map(applicant => {
          return (
            <div key={applicant.user.id}>
              <OpeningApplicantCard
                isUnread={!!applicant.application.messageThread?.isUnread}
                onCardClick={() => {
                  openVerifiedTalent(applicant.user.id);
                }}
                onOpenChat={() => {
                  if (isVerifiedUserApplicantType(applicant) || isOpeningApplicantType(applicant)) {
                    openOpeningApplicant(applicant.application.id);
                  }
                }}
                onDecline={() => {
                  const userMatchingProfileId = applicant.application.userMatchingProfile.id;

                  if (openingId && userMatchingProfileId) {
                    onReject({
                      openingId: openingId,
                      userMatchingProfileId: userMatchingProfileId,
                    });
                  }
                }}
                userData={applicant}
                wrapperClassName="opening-in-conversation__card"
              />
            </div>
          );
        })}

        {isOpeningApplicantsLoading && (
          <>
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </>
        )}
      </div>
    </div>
  );
};
